import {
  Node as SchemaNode,
  CallGroupExpansion,
  CallObject,
  CallQueueExpansion,
  CallRouteExpansion,
  DeviceExpansion,
  PhoneHoursExpansion,
  PhoneTreeExpansion,
  PlayMessageExpansion,
  VoicemailExpansion,
  RepeatExpansion,
  ForwardingNumberExpansion,
  NodeType,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { SchemaIO } from '@frontend/schema';
import {
  INFINITE_QUERY_ENDPOINT_NAMES,
  MUTATION_ENDPOINT_NAMES,
  QUERY_ENDPOINT_NAMES,
  STANDARD_QUERY_ENDPOINT_NAMES,
} from './constants';
import { SchemaCallRouteV1Service } from './service';

export { NodeType } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';

export type StandardQueryEndpointName = (typeof STANDARD_QUERY_ENDPOINT_NAMES)[number];
export type InfiniteQueryEndpointName = (typeof INFINITE_QUERY_ENDPOINT_NAMES)[number];
export type QueryEndpointName = (typeof QUERY_ENDPOINT_NAMES)[number];
export type ServiceStandardQueries = Pick<typeof SchemaCallRouteV1Service, StandardQueryEndpointName>;
export type ServiceInfiniteQueries = Pick<typeof SchemaCallRouteV1Service, InfiniteQueryEndpointName>;
export type ServiceQueries = Pick<typeof SchemaCallRouteV1Service, QueryEndpointName>;

export type MutationEndpointName = (typeof MUTATION_ENDPOINT_NAMES)[number];
export type ServiceMutations = Pick<typeof SchemaCallRouteV1Service, MutationEndpointName>;

export type CreateIO = SchemaIO<(typeof SchemaCallRouteV1Service)['Create']>;
export type UpdateIO = SchemaIO<(typeof SchemaCallRouteV1Service)['Update']>;
export type ListIO = SchemaIO<(typeof SchemaCallRouteV1Service)['List']>;
export type SaveIO = SchemaIO<(typeof SchemaCallRouteV1Service)['Save']>;
export type UpdatePhoneNumbersIO = SchemaIO<(typeof SchemaCallRouteV1Service)['UpdatePhoneNumbers']>;
export type DeleteIO = SchemaIO<(typeof SchemaCallRouteV1Service)['Delete']>;
export type UpdateExtensionsIO = SchemaIO<(typeof SchemaCallRouteV1Service)['UpdateExtensions']>;
export type ListPhoneHoursIO = SchemaIO<(typeof SchemaCallRouteV1Service)['ListPhoneHours']>;
export type ReadPhoneHoursIO = SchemaIO<(typeof SchemaCallRouteV1Service)['ReadPhoneHours']>;
export type UpdatePhoneHourIO = SchemaIO<(typeof SchemaCallRouteV1Service)['UpdatePhoneHour']>;
export type ReadIO = SchemaIO<(typeof SchemaCallRouteV1Service)['Read']>;

type SchemaNodeWithCorrectType = SchemaNode & {
  type: NodeType;
};

export type BaseNode = Pick<SchemaNodeWithCorrectType, 'id'>;
// export type Node = AllNodeTypes;
export type Node = AllNodeTypesCovered;

type AllNodeTypes =
  | UnspecifiedNode
  | CallGroupNode
  | CallQueueNode
  | CallRouteNode
  | ForwardDeviceNode
  | ForwardNumberNode
  | VoicemailBoxNode
  | PhoneTreeNode
  | RepeatNode
  | StartNode
  | TreeOptionNode
  | PlayMessageNode
  | OfficeHoursNode
  | OpenPhoneHoursNode
  | ClosedPhoneHoursNode
  | BreakPhoneHoursNode
  | FallbackNode
  | TerminateNode
  | BooleanNode;

/**
 * Type utility to ensure all NodeType enum values are covered by AllNodeTypes
 * This will produce a type error if a new NodeType is added to the enum
 * but not included in AllNodeTypes
 */
export type AllNodeTypesCovered = {
  [K in NodeType]: Extract<AllNodeTypes, { type: K }> extends never ? never : Extract<AllNodeTypes, { type: K }>;
}[NodeType];

type UnspecifiedNode = BaseNode & {
  type: NodeType.NODE_TYPE_UNSPECIFIED;
  callObject: BaseCallObject;
};

export type CallGroupNode = BaseNode & {
  type: NodeType.NODE_TYPE_CALL_GROUP;
  callObject: CallGroupCallObject;
};

export type CallQueueNode = BaseNode & {
  type: NodeType.NODE_TYPE_CALL_QUEUE;
  callObject: CallQueueCallObject;
};

export type CallRouteNode = BaseNode & {
  type: NodeType.NODE_TYPE_CALL_ROUTE;
  callObject: CallRouteCallObject;
};

export type ForwardDeviceNode = BaseNode & {
  type: NodeType.NODE_TYPE_FORWARD_DEVICE;
  callObject: ForwardDeviceCallObject;
};

export type ForwardNumberNode = BaseNode & {
  type: NodeType.NODE_TYPE_FORWARD_NUMBER;
  callObject: ForwardNumberCallObject;
};

export type VoicemailBoxNode = BaseNode & {
  type: NodeType.NODE_TYPE_VOICEMAIL_BOX;
  callObject: VoicemailBoxCallObject;
};

export type PhoneTreeNode = BaseNode & {
  type: NodeType.NODE_TYPE_PHONE_TREE;
  callObject: PhoneTreeCallObject;
};

export type RepeatNode = BaseNode & {
  type: NodeType.NODE_TYPE_REPEAT;
  callObject: RepeatCallObject;
};

export type StartNode = BaseNode & {
  type: NodeType.NODE_TYPE_START;
  callObject: StartCallObject;
};

export type TreeOptionNode = BaseNode & {
  type: NodeType.NODE_TYPE_TREE_OPTION;
  callObject: TreeOptionCallObject;
};

export type PlayMessageNode = BaseNode & {
  type: NodeType.NODE_TYPE_PLAY_MESSAGE;
  callObject: PlayMessageCallObject;
};

export type OfficeHoursNode = BaseNode & {
  type: NodeType.NODE_TYPE_PHONE_HOURS;
  callObject: OfficeHoursCallObject;
};

export type OpenPhoneHoursNode = BaseNode & {
  type: NodeType.NODE_TYPE_OPEN_HOURS;
  callObject: OpenPhoneHoursCallObject;
};

export type ClosedPhoneHoursNode = BaseNode & {
  type: NodeType.NODE_TYPE_CLOSED_HOURS;
  callObject: ClosedPhoneHoursCallObject;
};

export type BreakPhoneHoursNode = BaseNode & {
  type: NodeType.NODE_TYPE_BREAK_HOURS;
  callObject: BreakPhoneHoursCallObject;
};

export type FallbackNode = BaseNode & {
  type: NodeType.NODE_TYPE_FALLBACK;
  callObject: FallbackCallObject;
};

export type TerminateNode = BaseNode & {
  type: NodeType.NODE_TYPE_TERMINATE;
  callObject: TerminateCallObject;
};

export type BooleanNode = BaseNode & {
  type: NodeType.NODE_TYPE_BOOLEAN;
  callObject: BooleanCallObject;
};

type BaseCallObject = Pick<CallObject, 'primitiveId' | 'primitiveName' | 'instructionSetId' | 'instructionId'>;
type CallGroupCallObject = Extract<CallObject, { callGroupExpansion: CallGroupExpansion }>;
type CallQueueCallObject = Extract<CallObject, { callQueueExpansion: CallQueueExpansion }>;
type CallRouteCallObject = Extract<CallObject, { callRouteExpansion: CallRouteExpansion }>;
type ForwardDeviceCallObject = Extract<CallObject, { deviceExpansion: DeviceExpansion }>;
type ForwardNumberCallObject = Extract<CallObject, { forwardingNumberExpansion: ForwardingNumberExpansion }>;
type VoicemailBoxCallObject = Extract<CallObject, { voicemailExpansion: VoicemailExpansion }>;
type PhoneTreeCallObject = Extract<CallObject, { phoneTreeExpansion: PhoneTreeExpansion }>;
type RepeatCallObject = Extract<CallObject, { repeatExpansion: RepeatExpansion }>;
type StartCallObject = BaseCallObject;
type TreeOptionCallObject = BaseCallObject;
type PlayMessageCallObject = Extract<CallObject, { playMessageExpansion: PlayMessageExpansion }>;
type OfficeHoursCallObject = Extract<CallObject, { phoneHoursExpansion: PhoneHoursExpansion }>;
type OpenPhoneHoursCallObject = BaseCallObject;
type ClosedPhoneHoursCallObject = BaseCallObject;
type BreakPhoneHoursCallObject = BaseCallObject;
type FallbackCallObject = BaseCallObject;
type TerminateCallObject = BaseCallObject;
type BooleanCallObject = BaseCallObject;

export type NodeCallObject = Node['callObject'];

export const isNodeType = (type: string): type is NodeType => {
  return Object.values(NodeType).includes(type as NodeType);
};

export const isCallObject = (callObject: NodeCallObject): callObject is CallObject => {
  return (
    callObject &&
    callObject.instructionId !== undefined &&
    callObject.instructionSetId !== undefined &&
    callObject.primitiveId !== undefined &&
    callObject.primitiveName !== undefined
  );
};

export const isNode = (node: Node): node is Node => {
  return (
    node &&
    node.id !== undefined &&
    node.callObject !== undefined &&
    isNodeType(node.type) &&
    isCallObject(node.callObject)
  );
};

export function isNodeOfCorrectTypeAndCallObject(
  node: BaseNode & {
    type: Node['type'];
    callObject: NodeCallObject;
  }
): node is Node {
  const { type, callObject } = node;

  if (!node || !type || !node.id || !callObject) {
    return false;
  } else if (!isNodeType(type) || !isCallObject(callObject)) {
    return false;
  }

  switch (type) {
    case NodeType.NODE_TYPE_UNSPECIFIED:
      return false;
    case NodeType.NODE_TYPE_CALL_GROUP:
      return 'callGroupExpansion' in callObject;
    case NodeType.NODE_TYPE_CALL_QUEUE:
      return 'callQueueExpansion' in callObject;
    case NodeType.NODE_TYPE_CALL_ROUTE:
      return 'callRouteExpansion' in callObject;
    case NodeType.NODE_TYPE_FORWARD_DEVICE:
      return 'deviceExpansion' in callObject;
    case NodeType.NODE_TYPE_FORWARD_NUMBER:
      return 'forwardingNumberExpansion' in callObject;
    case NodeType.NODE_TYPE_VOICEMAIL_BOX:
      return 'voicemailExpansion' in callObject;
    case NodeType.NODE_TYPE_PHONE_TREE:
      return 'phoneTreeExpansion' in callObject;
    case NodeType.NODE_TYPE_REPEAT:
      return 'repeatExpansion' in callObject;
    case NodeType.NODE_TYPE_PLAY_MESSAGE:
      return 'playMessageExpansion' in callObject;
    case NodeType.NODE_TYPE_PHONE_HOURS:
      return 'phoneHoursExpansion' in callObject;
    // We can return true for the following node types since we have already checked in
    // the conditional statements above the switch statement, if they contain all the
    // required properties
    case NodeType.NODE_TYPE_OPEN_HOURS:
    case NodeType.NODE_TYPE_CLOSED_HOURS:
    case NodeType.NODE_TYPE_BREAK_HOURS:
    case NodeType.NODE_TYPE_FALLBACK:
    case NodeType.NODE_TYPE_START:
    case NodeType.NODE_TYPE_TREE_OPTION:
    case NodeType.NODE_TYPE_TERMINATE:
    case NodeType.NODE_TYPE_BOOLEAN:
      return true;
    default: {
      // This is an exhaustive check to ensure all node types are handled
      // If a new node type is added, TypeScript will throw an error here
      // because the type is not assignable to 'never'
      const _exhaustiveCheck: never = type;
      return _exhaustiveCheck;
    }
  }
}
